import { useQuery } from '@tanstack/react-query';
import { Card } from '../components/Common';
import { physiosApi } from '../services/api';

function formatLastUpdated(dateStr: string | null): string {
  if (!dateStr) return 'Never updated';

  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  return date.toLocaleDateString();
}

export function StatusBoard() {
  const { data: physios, isLoading, refetch } = useQuery({
    queryKey: ['physios'],
    queryFn: async () => {
      const response = await physiosApi.getAll();
      return response.data;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-500">Loading team status...</p>
        </div>
      </div>
    );
  }

  // Group physios by status
  const active = physios?.filter(p => p.status === 'active') || [];
  const sick = physios?.filter(p => p.status === 'sick') || [];
  const unavailable = physios?.filter(p => p.status === 'unavailable') || [];

  const totalCount = physios?.length || 0;
  const activeCount = active.length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team Status</h1>
          <p className="text-gray-500 mt-1">
            {activeCount} of {totalCount} physios active today
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          title="Refresh"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600">{active.length}</div>
            <div className="text-sm text-gray-500 mt-1">Active</div>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <div className="text-3xl font-bold text-red-600">{sick.length}</div>
            <div className="text-sm text-gray-500 mt-1">Sick</div>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <div className="text-3xl font-bold text-yellow-600">{unavailable.length}</div>
            <div className="text-sm text-gray-500 mt-1">Unavailable</div>
          </div>
        </Card>
      </div>

      {/* Active Physios */}
      {active.length > 0 && (
        <Card title={`Active (${active.length})`}>
          <div className="space-y-3">
            {active.map(physio => (
              <div
                key={physio.id}
                className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-100"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">✅</span>
                  <div>
                    <div className="font-medium text-gray-900">{physio.name}</div>
                    <div className="text-xs text-gray-500">{physio.postcode}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-500">
                    {formatLastUpdated(physio.updated_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Sick Physios */}
      {sick.length > 0 && (
        <Card title={`Sick (${sick.length})`}>
          <div className="space-y-3">
            {sick.map(physio => (
              <div
                key={physio.id}
                className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🤒</span>
                  <div>
                    <div className="font-medium text-gray-900">{physio.name}</div>
                    <div className="text-xs text-gray-500">{physio.postcode}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-500">
                    {formatLastUpdated(physio.updated_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Unavailable Physios */}
      {unavailable.length > 0 && (
        <Card title={`Unavailable (${unavailable.length})`}>
          <div className="space-y-3">
            {unavailable.map(physio => (
              <div
                key={physio.id}
                className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-100"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">⛔</span>
                  <div>
                    <div className="font-medium text-gray-900">{physio.name}</div>
                    <div className="text-xs text-gray-500">{physio.postcode}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-500">
                    {formatLastUpdated(physio.updated_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Empty state */}
      {totalCount === 0 && (
        <Card>
          <div className="text-center py-8 text-gray-500">
            No physios found. Add physios in the Physio Admin page.
          </div>
        </Card>
      )}

      {/* Info */}
      <div className="text-center text-sm text-gray-500">
        <p>Physios can update their status at <code className="bg-gray-100 px-1 rounded">/status</code></p>
        <p className="mt-1">Auto-refreshes every 30 seconds</p>
      </div>
    </div>
  );
}
